import { ChevronRight, FileText } from "@tamagui/lucide-icons";
import { Image } from "expo-image";
import { LayoutChangeEvent, Platform } from "react-native";
import { XStack, YStack } from "tamagui";

import type {
  UserInputContentList,
  UserInputFilePart,
  UserInputFilePartWithUrl,
  UserInputImagePart,
  UserInputImagePartWithUrl,
  UserInputTextPart,
} from "@/api/generated/models";
import { Button } from "@/components/Button";
import { Paragraph, Text } from "@/components/Typography";
import { useImageGalleryStore } from "@/store";
import { BLUR_HASH } from "@/theme/constants";
import { getFriendlyFileType } from "@/utils/attachments";

type UserInputContentListItem =
  | UserInputTextPart
  | UserInputImagePart
  | UserInputImagePartWithUrl
  | UserInputFilePart
  | UserInputFilePartWithUrl;

const isImagePart = (
  part: UserInputContentListItem,
): part is UserInputImagePartWithUrl => part.type === "input_image_with_url";

const isFilePart = (
  part: UserInputContentListItem,
): part is UserInputFilePartWithUrl => part.type === "input_file_with_url";

interface UserMessageProps {
  message: UserInputContentList;
  conversationType?: string;
  messageId?: string;
  onLayout?: (e: LayoutChangeEvent) => void;
}

const UserMessage = ({
  message,
  conversationType,
  messageId,
  onLayout,
}: UserMessageProps) => {
  const { openImageGallery } = useImageGalleryStore();

  const imageUris = message.filter(isImagePart).map((part) => part.url);
  const fileParts = message.filter(isFilePart);
  const displayedUris = imageUris.slice(-3);

  const imageStackTransform = (index: number, count: number) => {
    const isTop = index === count - 1;
    const isMiddle = count >= 3 && index === count - 2;
    const isBottom =
      (count >= 3 && index === count - 3) || (count === 2 && index === 0);

    const transform: any[] = [];
    if (!isTop) {
      if (isMiddle) {
        // second from top
        transform.push({ rotate: "6deg" });
        transform.push({ translateX: 2 });
        transform.push({ translateY: -7 });
      } else if (isBottom) {
        // bottom card (2 or 3 images)
        transform.push({ rotate: "-6deg" });
        transform.push({ translateX: -2 });
        transform.push({ translateY: -7 });
      }
    }
    return transform;
  };

  const textPart = message.find((part) => part.type === "input_text");

  return (
    <>
      <YStack
        id={messageId}
        data-purpose="UserMessage"
        alignSelf="flex-end"
        alignItems="flex-end"
        gap="$150"
        onLayout={onLayout}
        {...(conversationType === "curai" && {
          marginBottom: "$100",
          marginTop: "$100",
        })}
      >
        {imageUris.length > 0 && (
          <YStack>
            <Button.Wrapper
              dataTrackingId="user-image-preview"
              onPress={() =>
                openImageGallery(imageUris, imageUris[imageUris.length - 1])
              }
              aria-label={
                imageUris.length === 1
                  ? "Selected image, tap to view"
                  : `View ${imageUris.length} photos`
              }
            >
              <YStack
                width={224}
                height={232}
                justifyContent="center"
                alignItems="center"
              >
                {displayedUris.map((uri, index, arr) => {
                  return (
                    <Image
                      key={uri}
                      source={{ uri }}
                      placeholder={{ blurhash: BLUR_HASH }}
                      contentFit="cover"
                      transition={300}
                      style={{
                        width: 200,
                        height: 200,
                        borderRadius: 12,
                        position: "absolute",
                        transform: imageStackTransform(index, arr.length),
                      }}
                    />
                  );
                })}
              </YStack>
            </Button.Wrapper>
            {imageUris.length > 1 && (
              <Paragraph size="$sm" color="$textSecondary" textAlign="right">
                {imageUris.length} photos
              </Paragraph>
            )}
          </YStack>
        )}
        {fileParts.length > 0 && (
          <YStack width={322} gap="$100">
            {fileParts.map((part) => (
              <Button.Wrapper
                key={part.url}
                onPress={() => {
                  if (Platform.OS === "web") {
                    window.open(part.url, "_blank");
                  }
                }}
              >
                <XStack
                  width="100%"
                  backgroundColor="$fillNeutral"
                  borderColor="$shadowColor"
                  borderRadius="$100"
                  padding="$150"
                  gap="$150"
                  alignItems="center"
                >
                  <FileText size="$300" color="$textPrimary" flexShrink={0} />
                  <YStack flex={1}>
                    <Text
                      color="$textPrimary"
                      fontSize="$md"
                      fontWeight={700}
                      numberOfLines={1}
                      ellipsizeMode="middle"
                    >
                      {part.filename}
                    </Text>
                    <Text color="$textSecondary" fontSize="$sm">
                      {getFriendlyFileType(part.content_type)}
                    </Text>
                  </YStack>
                  <ChevronRight size="$200" color="$textSecondary" />
                </XStack>
              </Button.Wrapper>
            ))}
          </YStack>
        )}
        {textPart?.text && (
          <YStack
            backgroundColor="$fillPatient"
            paddingVertical="$100"
            paddingHorizontal="$200"
            borderRadius="$150"
          >
            <Paragraph color="$textPatient">{textPart?.text}</Paragraph>
          </YStack>
        )}
      </YStack>
    </>
  );
};

export default UserMessage;
